/**
 * Table localization for TanStack Table
 */

import type {TableLocalization} from '../../components/Table/Table';

export const TableLocalization_FR: TableLocalization = {
    actions: 'Actions',
    and: 'et',
    cancel: 'Annuler',
    changeFilterMode: 'Changer le mode de filtre',
    clearFilter: 'Effacer le filtre',
    clearSearch: 'Effacer la recherche',
    clearSort: 'Effacer le tri',
    clickToCopy: 'Cliquer pour copier',
    columnActions: 'Actions de colonne',
    copiedToClipboard: 'Copié dans le presse-papiers',
    edit: 'Modifier',
    expand: 'Développer',
    expandAll: 'Développer tout',
    filterByColumn: 'Filtrer par {column}',
    filterMode: 'Mode de filtre',
    filteringByColumn: 'Filtrage par {column}',
    goToFirstPage: 'Aller à la première page',
    goToLastPage: 'Aller à la dernière page',
    goToNextPage: 'Aller à la page suivante',
    goToPreviousPage: 'Aller à la page précédente',
    hide: 'Masquer',
    hideAll: 'Masquer tout',
    hideColumn: 'Masquer la colonne',
    rowsPerPage: 'Lignes par page',
    save: 'Enregistrer',
    search: 'Rechercher',
    sortByColumnAsc: 'Trier par {column} croissant',
    sortByColumnDesc: 'Trier par {column} décroissant',
    thenBy: 'puis par',
    toggleDensity: 'Basculer la densité',
    toggleFilters: 'Basculer les filtres',
    toggleFullScreen: 'Basculer le plein écran',
    toggleSelectAll: 'Tout sélectionner',
    toggleSelectRow: 'Sélectionner la ligne',
    toggleVisibility: 'Basculer la visibilité',
    ungroupByColumn: 'Dégrouper par {column}',
    unsorted: 'Non trié',
    of: 'sur',
};

export const TableLocalization_NL: TableLocalization = {
    actions: 'Acties',
    and: 'en',
    cancel: 'Annuleren',
    changeFilterMode: 'Filtermodus wijzigen',
    clearFilter: 'Filter wissen',
    clearSearch: 'Zoekopdracht wissen',
    clearSort: 'Sortering wissen',
    clickToCopy: 'Klik om te kopiëren',
    columnActions: 'Kolomacties',
    copiedToClipboard: 'Gekopieerd naar klembord',
    edit: 'Bewerken',
    expand: 'Uitvouwen',
    expandAll: 'Alles uitvouwen',
    filterByColumn: 'Filteren op {column}',
    filterMode: 'Filtermodus',
    filteringByColumn: 'Filteren op {column}',
    goToFirstPage: 'Ga naar eerste pagina',
    goToLastPage: 'Ga naar laatste pagina',
    goToNextPage: 'Ga naar volgende pagina',
    goToPreviousPage: 'Ga naar vorige pagina',
    hide: 'Verbergen',
    hideAll: 'Alles verbergen',
    hideColumn: 'Kolom verbergen',
    rowsPerPage: 'Rijen per pagina',
    save: 'Opslaan',
    search: 'Zoeken',
    sortByColumnAsc: 'Sorteren op {column} oplopend',
    sortByColumnDesc: 'Sorteren op {column} aflopend',
    thenBy: 'dan op',
    toggleDensity: 'Dichtheid wisselen',
    toggleFilters: 'Filters wisselen',
    toggleFullScreen: 'Volledig scherm wisselen',
    toggleSelectAll: 'Alles selecteren',
    toggleSelectRow: 'Rij selecteren',
    toggleVisibility: 'Zichtbaarheid wisselen',
    ungroupByColumn: 'Degroeperen op {column}',
    unsorted: 'Niet gesorteerd',
    of: 'van',
};

export const TableLocalization_EN: TableLocalization = {
    actions: 'Actions',
    and: 'and',
    cancel: 'Cancel',
    changeFilterMode: 'Change filter mode',
    clearFilter: 'Clear filter',
    clearSearch: 'Clear search',
    clearSort: 'Clear sort',
    clickToCopy: 'Click to copy',
    columnActions: 'Column actions',
    copiedToClipboard: 'Copied to clipboard',
    edit: 'Edit',
    expand: 'Expand',
    expandAll: 'Expand all',
    filterByColumn: 'Filter by {column}',
    filterMode: 'Filter mode',
    filteringByColumn: 'Filtering by {column}',
    goToFirstPage: 'Go to first page',
    goToLastPage: 'Go to last page',
    goToNextPage: 'Go to next page',
    goToPreviousPage: 'Go to previous page',
    hide: 'Hide',
    hideAll: 'Hide all',
    hideColumn: 'Hide column',
    rowsPerPage: 'Rows per page',
    save: 'Save',
    search: 'Search',
    sortByColumnAsc: 'Sort by {column} ascending',
    sortByColumnDesc: 'Sort by {column} descending',
    thenBy: 'then by',
    toggleDensity: 'Toggle density',
    toggleFilters: 'Toggle filters',
    toggleFullScreen: 'Toggle full screen',
    toggleSelectAll: 'Toggle select all',
    toggleSelectRow: 'Toggle select row',
    toggleVisibility: 'Toggle visibility',
    ungroupByColumn: 'Ungroup by {column}',
    unsorted: 'Unsorted',
    of: 'of',
};

/**
 * Get table localization based on language code
 */
export function getTableLocalization(language: string): TableLocalization {
    // Default to English if language is undefined or null
    if (!language) {
        return TableLocalization_EN;
    }
    
    const bcp47Language = language.includes('_') ? language.replace('_', '-') : language;
    
    switch (bcp47Language) {
        case 'fr':
        case 'fr-BE':
        case 'fr_BE':
            return TableLocalization_FR;
        case 'nl':
        case 'nl-BE':
        case 'nl_BE':
        case 'nl-NL':
        case 'nl_NL':
            return TableLocalization_NL;
        case 'en':
        case 'en-US':
        case 'en_US':
        default:
            return TableLocalization_EN;
    }
}

